import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Calendar, Plus, Trash2, Ban } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBlackoutDatesTab({ trips }) {
  const { token } = useAuth();
  const [blackoutDates, setBlackoutDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDate, setNewDate] = useState('');
  const [newReason, setNewReason] = useState('');
  const [selectedTripId, setSelectedTripId] = useState('all');

  useEffect(() => {
    fetchBlackoutDates();
  }, []);

  const fetchBlackoutDates = async () => {
    try {
      const res = await axios.get(`${API}/admin/blackout-dates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlackoutDates(res.data || []);
    } catch (err) {
      console.error('Failed to fetch blackout dates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBlackout = async () => {
    if (!newDate) {
      toast.error('Please select a date');
      return;
    }

    try {
      await axios.post(`${API}/admin/blackout-dates`, {
        date: newDate,
        reason: newReason,
        trip_id: selectedTripId === 'all' ? null : selectedTripId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Blackout date added');
      setNewDate('');
      setNewReason('');
      setSelectedTripId('all');
      fetchBlackoutDates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add blackout date');
    }
  };

  const handleDeleteBlackout = async (blackoutId) => {
    try {
      await axios.delete(`${API}/admin/blackout-dates/${blackoutId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Blackout date removed');
      fetchBlackoutDates();
    } catch (err) {
      toast.error('Failed to remove blackout date');
    }
  };

  // Helper to format date without timezone issues
  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTripName = (tripId) => {
    if (!tripId) return 'All Trips';
    const trip = trips?.find(t => t.id === tripId);
    return trip?.title || 'Unknown Trip';
  };

  // Group blackouts by month for better organization
  const groupedBlackouts = blackoutDates.reduce((groups, blackout) => {
    const [year, month] = blackout.date.split('-');
    const key = `${year}-${month}`;
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(blackout);
    return groups;
  }, {});

  const sortedMonths = Object.keys(groupedBlackouts).sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add New Blackout Date */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-red-500" />
            Add Blackout Date
          </CardTitle>
          <CardDescription>
            Block specific dates from being selected during booking. Useful for holidays, maintenance periods, or when you're unavailable.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-4 gap-4">
            <div>
              <Label className="text-sm mb-1 block">Date</Label>
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                data-testid="blackout-date-input"
              />
            </div>
            <div>
              <Label className="text-sm mb-1 block">Applies To</Label>
              <Select value={selectedTripId} onValueChange={setSelectedTripId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select trip" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Trips (Global)</SelectItem>
                  {trips?.map(trip => (
                    <SelectItem key={trip.id} value={trip.id}>{trip.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-1 block">Reason (Optional)</Label>
              <Input
                placeholder="e.g., Holiday, Maintenance"
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                data-testid="blackout-reason-input"
              />
            </div>
            <div className="flex items-end">
              <Button 
                onClick={handleAddBlackout}
                className="w-full bg-red-600 hover:bg-red-700"
                data-testid="add-blackout-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Blackout
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Blackout Dates */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-secondary" />
            Blocked Dates
            {blackoutDates.length > 0 && (
              <Badge variant="secondary">{blackoutDates.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {blackoutDates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No blackout dates set</p>
              <p className="text-sm">All dates are currently available for booking</p>
            </div>
          ) : (
            <div className="space-y-6">
              {sortedMonths.map(monthKey => {
                const [year, month] = monthKey.split('-');
                const monthDate = new Date(year, month - 1, 1);
                const monthLabel = monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                
                return (
                  <div key={monthKey}>
                    <h4 className="font-medium text-primary mb-3 border-b pb-2">{monthLabel}</h4>
                    <div className="grid gap-2">
                      {groupedBlackouts[monthKey]
                        .sort((a, b) => a.date.localeCompare(b.date))
                        .map(blackout => (
                          <div
                            key={blackout.id}
                            className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                <Ban className="h-5 w-5 text-red-600" />
                              </div>
                              <div>
                                <p className="font-medium text-primary">
                                  {formatDate(blackout.date)}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge variant="outline" className="text-xs">
                                    {getTripName(blackout.trip_id)}
                                  </Badge>
                                  {blackout.reason && (
                                    <span className="text-xs text-muted-foreground">
                                      • {blackout.reason}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-100"
                              onClick={() => handleDeleteBlackout(blackout.id)}
                              data-testid={`delete-blackout-${blackout.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
